<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else-if="!hasPermission" class="m-3">
    <access-denied />
  </div>
  <div v-else-if="featureUnavailable" class="m-3">
    <feature-not-available back-route="reports" back-text="_plan.Back to reports" />
  </div>
  <div v-else class="p-3">
    <!-- Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
      <div class="d-flex align-items-center gap-2">
        <div>
          <h5 class="fw-bold mb-0">
            <i class="fa-solid fa-trash-can me-2" style="color: #ef4444;"></i>{{ $t('Waste Report') }}
          </h5>
          <small class="text-muted">{{ $t('Inventory & Stock') }}</small>
        </div>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-primary btn-sm" @click="downloadPdf" :disabled="pdfLoading || loading">
          <i class="fa-solid fa-download me-1"></i>{{ $t('Download') }}
        </button>
        <button class="btn btn-primary btn-sm" @click="previewPdf" :disabled="pdfLoading || loading">
          <span v-if="pdfLoading"><i class="fa-solid fa-spinner fa-spin me-1"></i>{{ $t('Generating') }}...</span>
          <span v-else><i class="fa-solid fa-file-pdf me-1"></i>{{ $t('Preview PDF') }}</span>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body py-2 px-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <select v-model="filters.branch" class="form-select form-select-sm" style="min-width: 140px; max-width: 200px;" :disabled="loading">
            <option value="all">{{ $t('All Branches') }}</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
          <div class="input-group input-group-sm w-auto date-range-input-group">
            <span class="input-group-text"><i class="fa-regular fa-calendar"></i></span>
            <date-range-picker
              v-model="filters.date_range"
              :date-range="filters.date_range"
              :locale-data="dateRangeLocale"
              :ranges="dateRanges"
              :opens="'right'"
              :auto-apply="true"
              :show-dropdowns="true"
              :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate && picker.endDate">
                  {{ formatDate(picker.startDate) }} — {{ formatDate(picker.endDate) }}
                </span>
                <span v-else class="text-muted">{{ $t('Select date range') }}</span>
              </template>
            </date-range-picker>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span><i class="fa-solid fa-triangle-exclamation me-2"></i>{{ errorMessage }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="loadReport">{{ $t('Retry') }}</button>
    </div>

    <div :class="{ 'report-loading': loading }">

    <!-- KPI Cards -->
    <div class="row g-3 mb-4">
      <div class="col-6 col-md-4">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Total Waste Cost') }}</div>
            <div class="fw-bold fs-4 text-danger">{{ formatCurrency(summary.total_cost) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-4">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Waste Items') }}</div>
            <div class="fw-bold fs-4">{{ summary.total_items || 0 }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-4">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Waste Rate') }}</div>
            <div class="fw-bold fs-4 text-warning">{{ (summary.waste_rate || 0).toFixed(1) }}%</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Waste Items Table -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white border-0 py-3">
        <h6 class="mb-0 fw-semibold">
          <i class="fa-solid fa-trash-can me-2 text-danger"></i>{{ $t('Waste Items') }}
        </h6>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3">{{ $t('Item') }}</th>
                <th class="text-end">{{ $t('Quantity') }}</th>
                <th>{{ $t('Reason') }}</th>
                <th class="text-end pe-3">{{ $t('Cost') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!wasteData.length && !loading">
                <td colspan="4" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr v-for="(w, i) in wasteData" :key="w.inventory_id || i">
                <td class="ps-3 fw-medium">{{ w.item }}</td>
                <td class="text-end">{{ w.qty }} {{ $t(w.unit) }}</td>
                <td><span class="badge bg-light text-dark">{{ $t(w.reason) }}</span></td>
                <td class="text-end pe-3 text-danger fw-semibold">{{ formatCurrency(w.cost) }}</td>
              </tr>
            </tbody>
            <tfoot v-if="wasteData.length" class="table-light fw-bold">
              <tr>
                <td class="ps-3" colspan="3">{{ $t('Total') }}</td>
                <td class="text-end pe-3 text-danger">{{ formatCurrency(wasteTotalCost) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- Internal Consumption Section (staff meal + buffet usage) -->
    <div v-if="internalConsumption.items.length || internalConsumption.summary.total_cost" class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
        <h6 class="mb-0 fw-semibold">
          <i class="fa-solid fa-utensils me-2 text-info"></i>{{ $t('Internal Consumption') }}
          <span class="text-muted small fw-normal ms-2">{{ $t('Staff Meal + Buffet Usage') }}</span>
        </h6>
        <span class="badge bg-info-subtle text-info">
          {{ $t('Total') }}: {{ formatCurrency(internalConsumption.summary.total_cost) }}
        </span>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3">{{ $t('Item') }}</th>
                <th class="text-end">{{ $t('Quantity') }}</th>
                <th>{{ $t('Reason') }}</th>
                <th class="text-end pe-3">{{ $t('Cost') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!internalConsumption.items.length && !loading">
                <td colspan="4" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr v-for="(c, i) in internalConsumption.items" :key="c.inventory_id + '-' + i">
                <td class="ps-3 fw-medium">{{ c.item }}</td>
                <td class="text-end">{{ c.qty }} {{ $t(c.unit) }}</td>
                <td>
                  <span class="badge"
                        :class="c.type === 'buffet_usage' ? 'bg-info-subtle text-info' : 'bg-warning-subtle text-warning'">
                    {{ $t(c.reason) }}
                  </span>
                </td>
                <td class="text-end pe-3 fw-semibold">{{ formatCurrency(c.cost) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    </div><!-- /loading wrapper -->

    <div v-if="loading" class="report-loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('Loading') + '...' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { Reports } from '@/services/endpoint/reports.service';
import reportMixin from './reportMixin';

export default {
  name: 'ReportWaste',
  mixins: [reportMixin],
  data() {
    return {
      reportLabel: 'Waste Report',
      summary: { total_cost: 0, total_items: 0, waste_rate: 0 },
      items: [],
      internalConsumption: {
        summary: { total_cost: 0, total_items: 0 },
        items: [],
      },
    };
  },
  methods: {
    async fetchReport() {
      const res = await Reports.wasteReport(this.buildFetchParams());
      const payload = res?.data?.data || {};
      const filter  = res?.data?.filter || {};
      this.branches = filter.branches || [];
      this.summary  = payload.summary || { total_cost: 0, total_items: 0, waste_rate: 0 };
      this.items    = payload.items || [];
      this.internalConsumption = payload.internal_consumption || { summary: { total_cost: 0, total_items: 0 }, items: [] };
    },

    async buildPdfDefinition() {
      const content = [
        ...this.buildPdfHeader(),
        { text: this.$t('Waste Report'), style: 'sectionTitle' },
        {
          columns: [
            { text: [{ text: this.$t('Total Waste Cost') + '\n', style: 'kpiLabel' }, { text: this.formatCurrency(this.summary.total_cost), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('Waste Items') + '\n', style: 'kpiLabel' }, { text: String(this.summary.total_items || 0), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('Waste Rate') + '\n', style: 'kpiLabel' }, { text: `${(this.summary.waste_rate || 0).toFixed(1)}%`, style: 'kpiValue' }], width: '*' },
          ],
          margin: [0, 0, 0, 16],
        },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', '*', 'auto'],
            body: [
              [
                { text: this.$t('Item'), style: 'tableHeader' },
                { text: this.$t('Quantity'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Reason'), style: 'tableHeader' },
                { text: this.$t('Cost'), style: 'tableHeader', alignment: 'right' },
              ],
              ...this.wasteData.map(w => [
                { text: w.item, style: 'tableCell' },
                { text: `${w.qty} ${w.unit}`, style: 'tableCell', alignment: 'right' },
                { text: w.reason, style: 'tableCell' },
                { text: this.formatCurrencyRaw(w.cost), style: 'tableCell', alignment: 'right', color: '#ef4444' },
              ]),
            ],
          },
          layout: 'lightHorizontalLines',
        },
      ];

      if (this.internalConsumption.items.length) {
        content.push(
          { text: '\n' },
          { text: this.$t('Internal Consumption'), style: 'sectionTitle' },
          {
            text: `${this.$t('Total')}: ${this.formatCurrency(this.internalConsumption.summary.total_cost)}`,
            style: 'kpiLabel',
            margin: [0, 0, 0, 8],
          },
          {
            table: {
              headerRows: 1,
              widths: ['*', 'auto', '*', 'auto'],
              body: [
                [
                  { text: this.$t('Item'), style: 'tableHeader' },
                  { text: this.$t('Quantity'), style: 'tableHeader', alignment: 'right' },
                  { text: this.$t('Reason'), style: 'tableHeader' },
                  { text: this.$t('Cost'), style: 'tableHeader', alignment: 'right' },
                ],
                ...this.internalConsumption.items.map(c => [
                  { text: c.item, style: 'tableCell' },
                  { text: `${c.qty} ${c.unit}`, style: 'tableCell', alignment: 'right' },
                  { text: c.reason, style: 'tableCell' },
                  { text: this.formatCurrencyRaw(c.cost), style: 'tableCell', alignment: 'right' },
                ]),
              ],
            },
            layout: 'lightHorizontalLines',
          },
        );
      }

      return { content };
    },
  },
  computed: {
    wasteData() {
      return this.items || [];
    },
    wasteTotalCost() {
      return this.wasteData.reduce((s, w) => s + (w.cost || 0), 0);
    },
  },
};
</script>

<style scoped>
.report-loading {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.2s;
}
.report-loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1050;
}
</style>
